import { Money } from '../ui/Money';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Vault, HeartCrack } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from '../../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;
const aedText = (v) => `AED ${Math.round(Number(v || 0)).toLocaleString('en-US')}`;
const aed = (v) => <Money value={v} />;

const ACTION_META = {
  absconding: { label: 'Absconding', icon: Vault, vault: true },
  in_prison: { label: 'In Prison', icon: Vault, vault: true },
  death: { label: 'Death', icon: HeartCrack, vault: false },
};

/**
 * Captures the settlement for terminal statuses:
 * - Absconding / In Prison → dues moved to Staff Vault ("with company"), EOSB marked paid, visa frozen 6 months.
 * - Death → dues paid to next of kin (recipient details captured), EOSB marked paid.
 */
export const ExitSettlementDialog = ({ open, action, employeeId, employeeName, onDone, onCancel }) => {
  const meta = ACTION_META[action] || ACTION_META.absconding;
  const [eosb, setEosb] = useState(null);
  const [leaveSalary, setLeaveSalary] = useState('');
  const [pendingSalary, setPendingSalary] = useState('');
  const [nok, setNok] = useState({ name: '', relation: '', emirates_id: '', cheque_no: '', court_order_no: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !employeeId) return;
    setEosb(null); setLeaveSalary(''); setPendingSalary('');
    setNok({ name: '', relation: '', emirates_id: '', cheque_no: '', court_order_no: '' });
    axios.get(`${API}/api/dashboard/eosb/${employeeId}`)
      .then((r) => setEosb(Number(r.data?.net_payable ?? r.data?.eosb_amount ?? 0)))
      .catch(() => setEosb(0));
  }, [open, employeeId]);

  const total = (Number(eosb || 0)) + (parseFloat(leaveSalary) || 0) + (parseFloat(pendingSalary) || 0);

  const submit = async () => {
    if (action === 'death' && !nok.name.trim()) {
      toast({ title: 'Next of kin name is required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const body = {
        employee_id: employeeId,
        action,
        leave_salary: parseFloat(leaveSalary) || 0,
        pending_salary: parseFloat(pendingSalary) || 0,
      };
      if (action === 'death') body.next_of_kin = nok;
      const r = await axios.post(`${API}/api/staff-vault/settle`, body);
      if (meta.vault) {
        toast({ title: `${meta.label} recorded`, description: `${aedText(r.data?.total_amount)} moved to Staff Vault · EOSB marked paid · visa frozen until ${r.data?.visa_freeze_until}` });
      } else {
        toast({ title: 'Death settlement recorded', description: `${aedText(r.data?.total_amount)} paid to ${nok.name} · EOSB marked paid` });
      }
      onDone?.(meta.label);
    } catch (e) {
      toast({ title: 'Could not record settlement', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const Icon = meta.icon;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !saving) onCancel?.(); }}>
      <DialogContent className="max-w-md" data-testid="exit-settlement-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className="h-5 w-5 text-rose-600" /> {meta.label} — Settlement
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-gray-600">{employeeName}</p>

          <div className="rounded-lg border bg-gray-50 p-3 space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">EOSB (auto-calculated)</span>
              <span className="font-semibold">{eosb === null ? <Loader2 className="h-4 w-4 animate-spin" /> : aed(eosb)}</span>
            </div>
            <div className="flex items-center justify-between gap-2">
              <Label className="text-gray-600 font-normal">Leave salary balance</Label>
              <Input type="number" min="0" value={leaveSalary} onChange={(e) => setLeaveSalary(e.target.value)}
                className="w-32 h-8 text-right" placeholder="0" data-testid="settle-leave-salary" />
            </div>
            <div className="flex items-center justify-between gap-2">
              <Label className="text-gray-600 font-normal">Pending salary</Label>
              <Input type="number" min="0" value={pendingSalary} onChange={(e) => setPendingSalary(e.target.value)}
                className="w-32 h-8 text-right" placeholder="0" data-testid="settle-pending-salary" />
            </div>
            <div className="flex items-center justify-between border-t pt-2">
              <span className="font-semibold text-gray-800">Total dues</span>
              <span className="font-bold text-teal-700" data-testid="settle-total">{aed(total)}</span>
            </div>
          </div>

          {meta.vault ? (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
              Dues will be held in the <b>Staff Vault</b> ("with company"), EOSB marked fully paid, and the visa
              frozen for 6 months from the last working date.
            </p>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-gray-500">Recipient (next of kin) who received the payment:</p>
              <div className="grid grid-cols-2 gap-2">
                <div className="col-span-2">
                  <Label className="text-xs">Recipient name *</Label>
                  <Input value={nok.name} onChange={(e) => setNok({ ...nok, name: e.target.value })} data-testid="nok-name" />
                </div>
                <div>
                  <Label className="text-xs">Relation</Label>
                  <Input value={nok.relation} onChange={(e) => setNok({ ...nok, relation: e.target.value })} data-testid="nok-relation" />
                </div>
                <div>
                  <Label className="text-xs">Emirates ID</Label>
                  <Input value={nok.emirates_id} onChange={(e) => setNok({ ...nok, emirates_id: e.target.value })} data-testid="nok-eid" />
                </div>
                <div>
                  <Label className="text-xs">Cheque no.</Label>
                  <Input value={nok.cheque_no} onChange={(e) => setNok({ ...nok, cheque_no: e.target.value })} data-testid="nok-cheque" />
                </div>
                <div>
                  <Label className="text-xs">Court order no. <span className="text-gray-400">(optional)</span></Label>
                  <Input value={nok.court_order_no} onChange={(e) => setNok({ ...nok, court_order_no: e.target.value })} data-testid="nok-court-order" />
                </div>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onCancel?.()} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving || eosb === null} className="bg-rose-600 hover:bg-rose-700 gap-1.5" data-testid="settle-confirm-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />}
            {meta.vault ? 'Move to Vault & Freeze Visa' : 'Record Next-of-Kin Payment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExitSettlementDialog;
